import * as React from "react";

const timestampCrossFadeDurationInMilliseconds = 700;
const timestampFadeInDelayInMilliseconds = 500;
const timestampFadeInAnimationName = "quickJournalTimestampFadeIn";
const timestampFadeOutAnimationName = "quickJournalTimestampFadeOut";

interface IInputTimeStampProps {
    horizontalOffset: number;
    isHidden: boolean;
    isFocused: boolean;
    timeStamp: string;
    isCyberpunk?: boolean;
}

export function InputTimeStamp({
    horizontalOffset,
    isHidden,
    isFocused,
    timeStamp,
    isCyberpunk = false,
}: IInputTimeStampProps) {
    const [previousTimeStamp, setPreviousTimeStamp] =
        React.useState<string | null>(null);
    const [displayedTimeStamp, setDisplayedTimeStamp] =
        React.useState(timeStamp);
    const wasHiddenRef = React.useRef(isHidden);
    const [fadeInKey, setFadeInKey] = React.useState(isHidden ? 0 : 1);
    const [shouldFadeInFromHidden, setShouldFadeInFromHidden] =
        React.useState(false);

    React.useEffect(() => {
        if (timeStamp === displayedTimeStamp) {
            return;
        }

        if (isHidden) {
            setPreviousTimeStamp(null);
            setDisplayedTimeStamp(timeStamp);
            return;
        }

        setPreviousTimeStamp(displayedTimeStamp);
        setDisplayedTimeStamp(timeStamp);

        const timeoutId = window.setTimeout(
            () => setPreviousTimeStamp(null),
            timestampCrossFadeDurationInMilliseconds
        );

        return () => window.clearTimeout(timeoutId);
    }, [displayedTimeStamp, isHidden, timeStamp]);

    const shouldAnimateTimeStamp =
        !isHidden &&
        (!!previousTimeStamp || fadeInKey > 0 || shouldFadeInFromHidden);

    React.useLayoutEffect(() => {
        if (wasHiddenRef.current && !isHidden) {
            setFadeInKey((value) => value + 1);
            setShouldFadeInFromHidden(true);
        }

        if (isHidden) {
            setShouldFadeInFromHidden(false);
        }

        wasHiddenRef.current = isHidden;
    }, [isHidden]);

    return (
        <div
            style={{
                position: "absolute",
                left: "0",
                top: "6px",
                width: "70px",
                fontFamily: isCyberpunk ? "inherit" : "monospace",
                fontWeight: isCyberpunk ? "inherit" : undefined,
                fontVariantNumeric: "tabular-nums",
                fontSize: "16px",
                lineHeight: "29px",
                color: isCyberpunk ? "rgba(143, 247, 255, 0.65)" : "rgba(214, 201, 168, 0.7)",
                textShadow: isCyberpunk ? "0 0 3px rgba(0, 225, 255, 0.22)" : undefined,
                opacity:
                    shouldFadeInFromHidden || isHidden
                        ? 0
                        : isFocused
                          ? 1
                          : 0.5,
                pointerEvents: "none",
                textAlign: "right",
                userSelect: "none",
                transform: `translateX(${-horizontalOffset}px)`,
                transition: "opacity 350ms ease",
                animation: shouldFadeInFromHidden
                    ? `${timestampFadeInAnimationName} ${timestampCrossFadeDurationInMilliseconds}ms ease-out ${timestampFadeInDelayInMilliseconds}ms both`
                    : undefined,
            }}
        >
            {previousTimeStamp && !isHidden && (
                <span
                    key={previousTimeStamp}
                    style={{
                        position: "absolute",
                        inset: 0,
                        animation: `${timestampFadeOutAnimationName} ${timestampCrossFadeDurationInMilliseconds}ms ease-out forwards`,
                    }}
                >
                    {previousTimeStamp}
                </span>
            )}
            <span
                key={`${displayedTimeStamp}_${fadeInKey}`}
                style={{
                    position: "absolute",
                    inset: 0,
                    animation:
                        shouldAnimateTimeStamp && !isHidden && !shouldFadeInFromHidden
                            ? `${timestampFadeInAnimationName} ${timestampCrossFadeDurationInMilliseconds}ms ease-out both`
                            : undefined,
                    animationFillMode: shouldAnimateTimeStamp ? "both" : undefined,
                }}
            >
                {displayedTimeStamp}
            </span>
        </div>
    );
}
